import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { Color } from '@/constants/color';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { Person } from '@/types/person';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading/Heading';
import encounterTypeFromMapping from '@/utils/encounterTypeFromMapping';
import formatPersonName from '@/utils/formatPersonName';

import { Props } from './types';

const EncounterItem = ({ encounter }: Props): JSX.Element | null => {
  const { navigateToEncounterDetails } = useNavigateToPage();

  const { encounter_type, person, maybe_billable, name } = encounter;
  const encounterType = encounterTypeFromMapping(encounter_type);
  const encounterTypeTitle = encounterType?.label ?? encounter_type;
  const billableBadgeLabel = maybe_billable ? 'Maybe billable' : 'Non billable';
  const billableBadgeColor = maybe_billable ? Color.GREEN : Color.RED;
  const personFullName = person && formatPersonName({ person: person as Person });

  const navigateToDetails = useCallback(
    () => navigateToEncounterDetails(encounter),
    [encounter, navigateToEncounterDetails]
  );

  const renderPersonName = useCallback(() => {
    if (!person) return null;
    const clickablePersonName = (
      <Link aria-label="View Person" to={`/people/${person.id}`} className="text-blue-600">
        {personFullName}
        <FontAwesomeIcon className="ml-1" icon={faExternalLink} />
      </Link>
    );
    return <DataRow label="Person" content={clickablePersonName} />;
  }, [person, personFullName]);

  return (
    <>
      <Card className="p-2">
        <div className="flex justify-between mb-4">
          <Heading text={encounterTypeTitle} title={encounterTypeTitle} value={3} />
          <Badge color={billableBadgeColor}>
            <span className="text-center">{billableBadgeLabel}</span>
          </Badge>
        </div>
        <DataList>
          {renderPersonName()}
          <DataRow label="Name" content={name} />
        </DataList>
        <Button
          ariaLabel="View Encounter"
          className="mt-4 w-full"
          title="View Encounter"
          size="sm"
          onClick={navigateToDetails}>
          View
        </Button>
      </Card>
    </>
  );
};

export default EncounterItem;
